import { type FC, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/layout.js';
import { Login } from './pages/Login.js';
import { AgencyGate, PlatformGate } from './components/platform/route-guards.js';
import { DEVICE_CONTROL_ENABLED } from './lib/features.js';

const PlatformLayout = lazy(() => import('./components/platform/platform-layout.js').then((m) => ({ default: m.PlatformLayout })));
const PlatformDashboard = lazy(() => import('./pages/platform/PlatformDashboard.js').then((m) => ({ default: m.PlatformDashboard })));
const AgenciesPage = lazy(() => import('./pages/platform/AgenciesPage.js').then((m) => ({ default: m.AgenciesPage })));
const CreateAgencyPage = lazy(() => import('./pages/platform/CreateAgencyPage.js').then((m) => ({ default: m.CreateAgencyPage })));
const AgencyDetailPage = lazy(() => import('./pages/platform/AgencyDetailPage.js').then((m) => ({ default: m.AgencyDetailPage })));
const PlatformAuditPage = lazy(() => import('./pages/platform/PlatformAuditPage.js').then((m) => ({ default: m.PlatformAuditPage })));
const PlatformDevicesPage = lazy(() => import('./pages/platform/PlatformDevicesPage.js').then((m) => ({ default: m.PlatformDevicesPage })));
const Contact = lazy(() => import('./pages/Contact.js').then((m) => ({ default: m.Contact })));
const AcceptOwnerInvitation = lazy(() => import('./pages/AcceptOwnerInvitation.js').then((m) => ({ default: m.AcceptOwnerInvitation })));
const ForgotPassword = lazy(() => import('./pages/PasswordReset.js').then((m) => ({ default: m.ForgotPassword })));
const TermsOfService = lazy(() => import('./pages/Legal.js').then((m) => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/Legal.js').then((m) => ({ default: m.PrivacyPolicy })));
const ResetPassword = lazy(() => import('./pages/PasswordReset.js').then((m) => ({ default: m.ResetPassword })));
const Dashboard = lazy(() => import('./pages/Dashboard.js').then((m) => ({ default: m.Dashboard })));
const Creators = lazy(() => import('./pages/creators/Creators.js').then((m) => ({ default: m.Creators })));
const CreatorDetail = lazy(() => import('./pages/creators/CreatorDetail.js').then((m) => ({ default: m.CreatorDetail })));
const Devices = lazy(() => import('./pages/Devices.js').then((m) => ({ default: m.Devices })));
const Storage = lazy(() => import('./pages/Storage.js').then((m) => ({ default: m.Storage })));
const Agents = lazy(() => import('./pages/Agents.js').then((m) => ({ default: m.Agents })));
const Settings = lazy(() => import('./pages/Settings.js').then((m) => ({ default: m.Settings })));
const LiveDeviceWall = lazy(() => import('./pages/LiveDeviceWall.js').then((m) => ({ default: m.LiveDeviceWall })));
const ActivityLogs = lazy(() => import('./pages/ActivityLogs.js').then((m) => ({ default: m.ActivityLogs })));
const SystemHealth = lazy(() => import('./pages/SystemHealth.js').then((m) => ({ default: m.SystemHealth })));
const Streaming = lazy(() => import('./pages/Streaming.js').then((m) => ({ default: m.Streaming })));
const CreatorAuthorizationPage = lazy(() => import('./pages/creators/CreatorAuthorizationPage.js').then((m) => ({ default: m.CreatorAuthorizationPage })));
const CreatorApprovalQueuePage = lazy(() => import('./pages/creators/CreatorApprovalQueuePage.js').then((m) => ({ default: m.CreatorApprovalQueuePage })));
const ContentApprovalManagementPage = lazy(() => import('./pages/creators/ContentApprovalManagementPage.js').then((m) => ({ default: m.ContentApprovalManagementPage })));
const ScheduledPostsPage = lazy(() => import('./pages/ScheduledPostsPage.js').then((m) => ({ default: m.ScheduledPostsPage })));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })));
const ContentBrowser = lazy(() => import('./pages/hub/ContentBrowser.js').then((m) => ({ default: m.ContentBrowser })));
const UploadPage = lazy(() => import('./pages/hub/UploadPage.js').then((m) => ({ default: m.UploadPage })));
const AssetDetailPage = lazy(() => import('./pages/hub/AssetDetailPage.js').then((m) => ({ default: m.AssetDetailPage })));
const CollectionsPage = lazy(() => import('./pages/hub/CollectionsPage.js').then((m) => ({ default: m.CollectionsPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5000, retry: 2 },
  },
});

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center p-12">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute() {
  return (
    <AgencyGate>
      <Layout />
    </AgencyGate>
  );
}

function PlatformRoute() {
  return (
    <PlatformGate>
      <Suspense fallback={<PageLoader />}>
        <PlatformLayout />
      </Suspense>
    </PlatformGate>
  );
}

export const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/contact" element={<Suspense fallback={<div className="min-h-screen bg-slate-950"><PageLoader /></div>}><Contact /></Suspense>} />
          <Route path="/accept-owner-invitation" element={<Suspense fallback={<PageLoader />}><AcceptOwnerInvitation /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<div className="min-h-screen bg-slate-950"><PageLoader /></div>}><TermsOfService /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<div className="min-h-screen bg-slate-950"><PageLoader /></div>}><PrivacyPolicy /></Suspense>} />
          <Route path="/platform" element={<PlatformRoute />}>
            <Route index element={<Navigate to="/platform/dashboard" replace />} />
            <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><PlatformDashboard /></Suspense>} />
            <Route path="agencies" element={<Suspense fallback={<PageLoader />}><AgenciesPage /></Suspense>} />
            <Route path="agencies/new" element={<Suspense fallback={<PageLoader />}><CreateAgencyPage /></Suspense>} />
            <Route path="agencies/:id" element={<Suspense fallback={<PageLoader />}><AgencyDetailPage /></Suspense>} />
            <Route path="audit" element={<Suspense fallback={<PageLoader />}><PlatformAuditPage /></Suspense>} />
            <Route path="devices" element={<Suspense fallback={<PageLoader />}><PlatformDevicesPage /></Suspense>} />
            <Route path="*" element={<Navigate to="/platform/dashboard" replace />} />
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
            <Route path="/creators" element={<Suspense fallback={<PageLoader />}><Creators /></Suspense>} />
            <Route path="/creators/:id" element={<Suspense fallback={<PageLoader />}><CreatorDetail /></Suspense>} />
            <Route path="/creator/authorization" element={<Suspense fallback={<PageLoader />}><CreatorAuthorizationPage /></Suspense>} />
            <Route path="/creator/content-approvals" element={<Suspense fallback={<PageLoader />}><CreatorApprovalQueuePage /></Suspense>} />
            <Route path="/content/approvals" element={<Suspense fallback={<PageLoader />}><ContentApprovalManagementPage /></Suspense>} />
            <Route path="/posts" element={<Suspense fallback={<PageLoader />}><ScheduledPostsPage /></Suspense>} />
            <Route path="/devices" element={<Suspense fallback={<PageLoader />}><Devices /></Suspense>} />
            {/* Device-control pages are off while the kill switch is set to false (lib/features.ts). */}
            <Route path="/devices/live-wall" element={DEVICE_CONTROL_ENABLED ? <Suspense fallback={<PageLoader />}><LiveDeviceWall /></Suspense> : <Navigate to="/devices" replace />} />
            {/* Screens that did not work end to end are hidden (2026-11-04, focus on
                the core workflow): their addresses forward to the nearest working page.
                Storage and Streaming are off the menu but still open by address. */}
            <Route path="/content" element={<Navigate to="/hub/content" replace />} />
            <Route path="/media" element={<Navigate to="/hub/content" replace />} />
            <Route path="/scheduler" element={<Navigate to="/posts" replace />} />
            <Route path="/automation" element={<Navigate to="/posts" replace />} />
            <Route path="/automation/new" element={<Navigate to="/posts" replace />} />
            <Route path="/analytics" element={<Navigate to="/" replace />} />
            <Route path="/ai" element={<Navigate to="/settings" replace />} />
            <Route path="/storage" element={<Suspense fallback={<PageLoader />}><Storage /></Suspense>} />
            <Route path="/notifications" element={<Navigate to="/activity" replace />} />
            <Route path="/activity" element={<Suspense fallback={<PageLoader />}><ActivityLogs /></Suspense>} />
            <Route path="/integrity" element={<Navigate to="/" replace />} />
            <Route path="/health" element={<Suspense fallback={<PageLoader />}><SystemHealth /></Suspense>} />
            <Route path="/streaming" element={DEVICE_CONTROL_ENABLED ? <Suspense fallback={<PageLoader />}><Streaming /></Suspense> : <Navigate to="/devices" replace />} />
            <Route path="/history" element={<Navigate to="/posts" replace />} />
            {/* The old Accounts page read a legacy table that is empty in
                production; connected accounts live on each creator's page. */}
            <Route path="/accounts" element={<Navigate to="/creators" replace />} />
            <Route path="/agents" element={<Suspense fallback={<PageLoader />}><Agents /></Suspense>} />
            <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
            {/* Content Hub folded into the Media Library (2026-11-04). */}
            <Route path="/hub" element={<Navigate to="/hub/content" replace />} />
            <Route path="/hub/content" element={<Suspense fallback={<PageLoader />}><ContentBrowser /></Suspense>} />
            <Route path="/hub/content/:id" element={<Suspense fallback={<PageLoader />}><AssetDetailPage /></Suspense>} />
            <Route path="/hub/upload" element={<Suspense fallback={<PageLoader />}><UploadPage /></Suspense>} />
            <Route path="/hub/collections" element={<Suspense fallback={<PageLoader />}><CollectionsPage /></Suspense>} />
            <Route path="/audit" element={<Suspense fallback={<PageLoader />}><AuditLogPage /></Suspense>} />
            {/* An unknown address (e.g. the old header search, /search) opened a blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};
